import clsx from "clsx";
import { useCallback, useEffect, useMemo } from "react";
import Box from "@/ui/atoms/Box";
import type { BoxProps } from "@/ui/atoms/Box";
import Typography from "@/ui/atoms/Typography";
import type { TChartOfAccountsDefinition } from "@/apis/odin/apiODIN";
import type { UserDatabase } from "@/ui/organisms/userTypes";
import Card from "../../molecules/Card";
import { Button, MultiSelect } from "@proda-ai/ui-kit";
import { isFalsy, isTruthy } from "@/utils/guards";
import ErrorMessage from "@/ui/atoms/ErrorMessage";
import InfoMessage from "@/ui/atoms/InfoMessage";
import Loader from "@/ui/atoms/Loader";
import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";

export type AdminPanelProps = BoxProps & {
    handleSubmitAddAccountPermissions: () => Promise<void>;
    handleSubmitDeleteAccountPermissions: () => Promise<void>;
    setDashboardSelectedDatabaseIds: (databaseIds: number[]) => void;
    setDashboardSelectedCoAIds: (coaIds: number[]) => void;
    adminDatabases: Record<string, UserDatabase[]>;
    dashboardSelectedDatabaseIds: number[] | null;
    dashboardSelectedCoAIds: number[] | null;
    availableCoAsForDatabaseIdState:
        | "idle"
        | "pending"
        | "resolved"
        | "rejected";
    availableCoAsForDatabaseId: TChartOfAccountsDefinition[];
    userDataState: "idle" | "pending" | "resolved" | "rejected";
    coasRequestStatus: "idle" | "pending" | "resolved" | "rejected";
    getAdminDatabasesState: "idle" | "pending" | "resolved" | "rejected";
    onAdminMount: () => void;
    onAdminUnmount: () => void;
};

const AdminPanel = ({
    sx,
    className,
    setDashboardSelectedDatabaseIds,
    setDashboardSelectedCoAIds,
    handleSubmitAddAccountPermissions,
    handleSubmitDeleteAccountPermissions,
    onAdminMount,
    onAdminUnmount,
    adminDatabases,
    availableCoAsForDatabaseIdState,
    availableCoAsForDatabaseId,
    userDataState,
    coasRequestStatus,
    getAdminDatabasesState,
    dashboardSelectedDatabaseIds,
    dashboardSelectedCoAIds,
    ...otherProps
}: AdminPanelProps) => {
    useEffect(() => {
        onAdminMount();
        return () => {
            onAdminUnmount();
        };
    }, [onAdminMount, onAdminUnmount]);

    const noDatabaseSelected =
        !isFalsy(dashboardSelectedDatabaseIds) &&
        dashboardSelectedDatabaseIds.length === 0;
    const resetSelectedCoAIdsWhenNoDatabaseSelected = useCallback(() => {
        if (noDatabaseSelected) {
            setDashboardSelectedCoAIds([]);
        }
    }, [noDatabaseSelected, setDashboardSelectedCoAIds]);
    useEffect(() => {
        resetSelectedCoAIdsWhenNoDatabaseSelected();
    }, [resetSelectedCoAIdsWhenNoDatabaseSelected]);

    const isError =
        availableCoAsForDatabaseIdState === "rejected" ||
        userDataState === "rejected" ||
        coasRequestStatus === "rejected" ||
        getAdminDatabasesState === "rejected";
    const isLoading =
        getAdminDatabasesState === "pending" ||
        userDataState === "pending" ||
        coasRequestStatus === "pending" ||
        getAdminDatabasesState === "idle" ||
        userDataState === "idle" ||
        coasRequestStatus === "idle";
    const noData =
        !isError &&
        !isLoading &&
        Object.values(adminDatabases).flat().length === 0;

    const databaseOptions = useMemo(
        () =>
            Object.entries(adminDatabases).map(([companyName, databases]) => ({
                groupLabel: companyName,
                options: databases.map(({ databaseId, databaseName }) => ({
                    value: databaseId,
                    label: databaseName,
                })),
            })),
        [adminDatabases],
    );

    const coaOptions = useMemo(() => {
        return availableCoAsForDatabaseId.map(({ coaId, name }) => ({
            value: coaId,
            label: name,
        }));
    }, [availableCoAsForDatabaseId]);

    const isRevokable =
        !isLoading &&
        !isFalsy(dashboardSelectedDatabaseIds) &&
        !isFalsy(dashboardSelectedCoAIds) &&
        availableCoAsForDatabaseId.filter(
            coa =>
                dashboardSelectedCoAIds.includes(coa.coaId) &&
                isTruthy(coa.isRevokable),
        ).length === dashboardSelectedCoAIds.length &&
        availableCoAsForDatabaseId.filter(
            coa =>
                dashboardSelectedCoAIds.includes(coa.coaId) &&
                isTruthy(coa.isAssigned),
        ).length > 0;

    const isAssignable =
        !isLoading &&
        !isFalsy(dashboardSelectedDatabaseIds) &&
        !isFalsy(dashboardSelectedCoAIds) &&
        availableCoAsForDatabaseId.filter(
            coa =>
                dashboardSelectedCoAIds.includes(coa.coaId) &&
                isFalsy(coa.isAssigned),
        ).length > 0;

    return (
        <>
            {isLoading && (
                <LayoutFillParentCentered>
                    <Loader
                        sx={{ marginTop: "20vh" }}
                        label="Loading admin panel data..."
                    />
                </LayoutFillParentCentered>
            )}
            {isError && (
                <ErrorMessage sx={{ width: "fit-content" }}>
                    Unable to load admin panel data. Please try again later.
                </ErrorMessage>
            )}

            {noData && !isLoading && (
                <InfoMessage sx={{ width: "fit-content" }}>
                    No admin panel data available.
                </InfoMessage>
            )}

            {!isError && !noData && !isLoading && (
                <Card
                    sx={{
                        width: "414px",
                        marginY: "80px",
                        maxWidth: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        ...sx,
                    }}
                    data-testid="AdminPanel"
                    className={clsx("AdminPanel", className)}
                    {...otherProps}
                >
                    <Box sx={{ paddingBottom: "24px" }}>
                        <Typography variant="h1">Admin</Typography>
                    </Box>

                    <Typography variant="h3">
                        Assign Chart(s) of Accounts to Database(s)
                    </Typography>

                    <Box sx={{ pt: 2, pb: 2, width: "100%" }}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                    zIndex: 1,
                                }}
                            >
                                <MultiSelect
                                    hasSelectAll={false}
                                    options={databaseOptions}
                                    selectedValues={
                                        dashboardSelectedDatabaseIds
                                            ? databaseOptions
                                                  .flatMap(
                                                      group => group.options,
                                                  )
                                                  .filter(opt =>
                                                      dashboardSelectedDatabaseIds.includes(
                                                          opt.value,
                                                      ),
                                                  )
                                            : []
                                    }
                                    setSelectedValues={values =>
                                        void setDashboardSelectedDatabaseIds(
                                            values.map(v => v.value),
                                        )
                                    }
                                    getOptionId={option => option.value}
                                    getOptionLabel={option => option.label}
                                    props={{
                                        MultiSelectInput: {
                                            placeholder: "Select databases...",
                                        },
                                    }}
                                    selectedValuesString={
                                        dashboardSelectedDatabaseIds &&
                                        dashboardSelectedDatabaseIds.length
                                            ? `Selected ${dashboardSelectedDatabaseIds.length} database${
                                                  dashboardSelectedDatabaseIds.length >
                                                  1
                                                      ? "s"
                                                      : ""
                                              }`
                                            : "No databases selected"
                                    }
                                />
                            </Box>
                            <Box
                                sx={{
                                    position: "relative",
                                    zIndex: 0,
                                }}
                            >
                                <MultiSelect
                                    hasSelectAll={false}
                                    options={coaOptions}
                                    selectedValues={
                                        dashboardSelectedCoAIds
                                            ? coaOptions.filter(opt =>
                                                  dashboardSelectedCoAIds.includes(
                                                      opt.value,
                                                  ),
                                              )
                                            : []
                                    }
                                    setSelectedValues={values =>
                                        setDashboardSelectedCoAIds(
                                            values.map(v => v.value),
                                        )
                                    }
                                    getOptionId={option => option.value}
                                    getOptionLabel={option => option.label}
                                    props={{
                                        MultiSelectInput: {
                                            placeholder: "Select CoA...",
                                            disabled: noDatabaseSelected,
                                        },
                                    }}
                                    selectedValuesString={
                                        dashboardSelectedCoAIds &&
                                        dashboardSelectedCoAIds.length
                                            ? `Selected ${dashboardSelectedCoAIds.length} CoA${
                                                  dashboardSelectedCoAIds.length >
                                                  1
                                                      ? "s"
                                                      : ""
                                              }`
                                            : "No CoA selected"
                                    }
                                />
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: 2,
                                }}
                            >
                                <Button
                                    style={{ width: "100px" }}
                                    pattern="danger"
                                    onClick={() =>
                                        void handleSubmitDeleteAccountPermissions()
                                    }
                                    disabled={!isRevokable}
                                >
                                    Revoke
                                </Button>
                                <Button
                                    style={{ width: "100px" }}
                                    onClick={() =>
                                        void handleSubmitAddAccountPermissions()
                                    }
                                    disabled={!isAssignable}
                                >
                                    Grant
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Card>
            )}
        </>
    );
};

export default AdminPanel;
